import React from 'react';
import { Tabs, Button, Tooltip, Space } from 'antd';
import { PlusOutlined, CloseOutlined, MessageOutlined } from '@ant-design/icons';
import type { ChatConversation } from '@/services/chat';

export interface ChatTabsProps {
  conversations: ChatConversation[];
  activeConversationId?: string;
  onTabChange: (conversationId: string) => void;
  onNewConversation: () => void;
  onCloseConversation: (conversationId: string) => void;
  loading?: boolean;
  maxTabs?: number;
}

export const ChatTabs: React.FC<ChatTabsProps> = ({
  conversations = [],
  activeConversationId,
  onTabChange,
  onNewConversation,
  onCloseConversation,
  loading = false,
  maxTabs = 5,
}) => {
  
  const generateTabTitle = (conversation: ChatConversation): string => {
    if (conversation.title) {
      return conversation.title;
    }
    
    // Generate title from first user message
    const firstUserMessage = conversation.messages.find(msg => msg.isUser);
    if (firstUserMessage?.content) {
      const maxLength = 20;
      const content = firstUserMessage.content.trim();
      return content.length > maxLength 
        ? content.substring(0, maxLength) + '...' 
        : content;
    }
    
    return 'New Chat';
  };

  const handleTabEdit = (
    targetKey: React.MouseEvent | React.KeyboardEvent | string,
    action: 'add' | 'remove'
  ) => {
    if (action === 'add') {
      onNewConversation();
    } else if (action === 'remove' && typeof targetKey === 'string') {
      onCloseConversation(targetKey);
    }
  };

  const handleTabChange = (activeKey: string) => {
    onTabChange(activeKey);
  };

  const canAddNewTab = conversations.length < maxTabs;

  const tabBarExtraContent = (
    <Space size={4}>
      <Tooltip title={canAddNewTab ? "New conversation" : `Maximum ${maxTabs} conversations allowed`}>
        <Button
          type="text"
          size="small"
          icon={<PlusOutlined />}
          onClick={onNewConversation}
          disabled={!canAddNewTab || loading}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 24,
            width: 24,
            padding: 0,
          }}
        />
      </Tooltip>
    </Space>
  );

  const tabItems = conversations.map((conversation) => {
    const title = generateTabTitle(conversation);
    const hasMessages = conversation.messages.length > 0;
    const lastMessage = conversation.messages[conversation.messages.length - 1];
    const isActiveStreaming = lastMessage?.isStreaming && !lastMessage?.isUser;
    
    return {
      key: conversation.id.toString(),
      label: (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          maxWidth: 120,
        }}>
          <MessageOutlined 
            style={{ 
              fontSize: 12,
              color: hasMessages ? '#1890ff' : '#d9d9d9',
            }} 
          />
          <span style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: 12,
          }}>
            {title}
          </span>
          {isActiveStreaming && (
            <div style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              // backgroundColor: '#52c41a',
              animation: 'pulse 2s infinite',
            }} />
          )}
        </div>
      ),
      children: null, // Content will be handled by parent component
      closable: conversations.length > 1, // Don't allow closing the last tab
    };
  });

  // Ensure we have at least one tab
  if (tabItems.length === 0) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={onNewConversation}
          loading={loading}
        >
          Start New Conversation
        </Button>
      </div>
    );
  }

  return (
    <div style={{ 
      position: 'sticky',
      top: 0,
      zIndex: 10,
      // backgroundColor: '#fafafa',
    }}>
      <Tabs
        type="editable-card"
        activeKey={activeConversationId?.toString()}
        onChange={handleTabChange}
        onEdit={handleTabEdit}
        items={tabItems}
        tabBarExtraContent={tabBarExtraContent}
        size="small"
        hideAdd={!canAddNewTab}
        tabBarStyle={{
          margin: 0,
          padding: '0 8px',
          // backgroundColor: '#fafafa',
        }}
        tabBarGutter={4}
      />
      
      {/* CSS for pulse animation and tab styling */}
      <style>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }
        .ant-tabs-small .ant-tabs-tab {
          font-size: 12px;
        }
        .ant-tabs-small .ant-tabs-tab-btn {
          font-size: 12px;
        }
      `}</style>
    </div>
  );
};

export default ChatTabs;
